import React, { useState } from "react";
import {
    useGetPendingWithdrawalsQuery,
    useConfirmWithdrawalMutation,
    useCancelWithdrawalMutation,
    useGetPendingAlnyWithdrawalsQuery,
    useConfirmAlnyWithdrawalMutation,
    useCancelAlnyWithdrawalMutation,
    useGetWithdrawalHistoryQuery,
    useCreateAdminAltynCryptoPayoutMutation,
    useCreateAdminAltynSbpPayoutMutation,
    useFreezeWithdrawalMutation,
} from "../api";
import type {
    WithdrawalRequest,
    WithdrawalStatus,
    WithdrawalHistoryItem,
} from "../api/withdrawals";
import { UserViewModal } from "../components/UserViewModal";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

export const AdminWithdrawals: React.FC = () => {
    const { showError, showSuccess, notification, hideNotification } = useNotification();
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isUserViewModalOpen, setIsUserViewModalOpen] = useState(false);

    const [selectedWithdrawal, setSelectedWithdrawal] =
        useState<WithdrawalRequest | null>(null);
    const [cancelComment, setCancelComment] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelCurrency, setCancelCurrency] = useState(0);
    const [historyStatus, setHistoryStatus] = useState<"all" | WithdrawalStatus>(
        "all"
    );
    const [historyCurrency, setHistoryCurrency] = useState<
        "all" | "USD" | "USDT"
    >("all");
    const [historySource, setHistorySource] = useState<"all" | "hub" | "game">(
        "all"
    );
    const [historyPage, setHistoryPage] = useState(1);

    // Загрузка запросов на вывод
    const { data: withdrawalsResponse } =
        useGetPendingWithdrawalsQuery(undefined, {
            pollingInterval: 120000, // Обновление каждые 120 секунд
        });
    // --- ALNY ---
    const { data: alnyResponse } = useGetPendingAlnyWithdrawalsQuery(
        undefined,
        {
            pollingInterval: 120000,
        }
    );
    const [confirmAlny] = useConfirmAlnyWithdrawalMutation();
    const [cancelAlny] = useCancelAlnyWithdrawalMutation();

    const alnyRows = alnyResponse?.data ?? [];


    const handleConfirmAlny = async (user_id: number, sum?: number) => {
        if (!window.confirm("Подтвердить вывод ALNY?")) return;
        try {
            await confirmAlny({ user_id, sum }).unwrap();
            showSuccess("ALNY вывод подтверждён");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const handleCancelAlny = async (user_id: number) => {
        const comment = window.prompt("Причина отмены", "");
        if (comment == null) return;
        try {
            await cancelAlny({
                user_id,
                comment: comment || undefined,
            }).unwrap();
            showSuccess("ALNY вывод отменён");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const withdrawals = withdrawalsResponse?.data?.withdrawals ?? [];
    // Список активных заявок показываем поштучно
    const fiatWithdrawals = withdrawals;

    // История заявок (отдельная таблица)
    const {
        data: historyResponse,
        isLoading: isHistoryLoading,
    } = useGetWithdrawalHistoryQuery({
        page: historyPage,
        per_page: 20,
        status: historyStatus === "all" ? undefined : historyStatus,
        currency: historyCurrency === "all" ? undefined : historyCurrency,
        source: historySource === "all" ? undefined : historySource,
    });

    const historyItems: WithdrawalHistoryItem[] =
        historyResponse?.data?.items ?? [];
    const historyMeta = historyResponse?.data?.meta;

    // Мутации
    const [confirm, { isLoading: isConfirming }] = useConfirmWithdrawalMutation();
    const [cancel, { isLoading: isCancelling }] = useCancelWithdrawalMutation();
    const [createAltynCryptoPayout, { isLoading: isCreatingAltynCryptoPayout }] =
        useCreateAdminAltynCryptoPayoutMutation();
    const [createAltynSbpPayout, { isLoading: isCreatingAltynSbpPayout }] =
        useCreateAdminAltynSbpPayoutMutation();
    const [freeze] = useFreezeWithdrawalMutation();

    const handleFreeze = async (id: number) => {
        try {
            await freeze({ id }).unwrap();
            showSuccess("Данные открыты, заявка заморожена для пользователя");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const handleConfirm = async (
        withdrawal: WithdrawalRequest,
        currency: number,
        account?: string,
        card?: string
    ) => {
        if (isConfirming) return;

        // CryptoBot без telegram_auto — защита: явное согласие админа
        let forceCryptobot = false;
        if (currency === 1 && !withdrawal.telegram_auto) {
            const tg = withdrawal.telegram_id || "не указан";
            const ok = window.confirm(
                `У пользователя выключен telegram_auto (защита от авто-выплат на CryptoBot).\n\n` +
                    `${withdrawal.surname} ${withdrawal.name} (ID ${withdrawal.user_id})\n` +
                    `Telegram ID: ${tg}\n` +
                    `Сумма: ${withdrawal.amount} USDT\n\n` +
                    `Вы уверены, что хотите отправить средства на CryptoBot этому пользователю?`
            );
            if (!ok) return;
            forceCryptobot = true;
        } else if (!window.confirm("Подтвердить вывод?")) {
            return;
        }

        try {
            await confirm({
                request_id: withdrawal.id,
                currency,
                account,
                card,
                ...(forceCryptobot ? { force_cryptobot: true } : {}),
            }).unwrap();
            showSuccess("Вывод подтверждён");
        } catch (error: any) {
            // API теж може попросити згоду (якщо фронт без telegram_auto у відповіді)
            if (
                currency === 1 &&
                !forceCryptobot &&
                (error?.status === 409 || error?.data?.code === "CRYPTOBOT_CONSENT_REQUIRED")
            ) {
                const d = error?.data?.data ?? {};
                const tg = d.telegram_id || withdrawal.telegram_id || "не указан";
                const ok = window.confirm(
                    `${error?.data?.message || "Нужно подтверждение админа."}\n\n` +
                        `Telegram ID: ${tg}\nСумма: ${d.amount ?? withdrawal.amount} USDT\n\n` +
                        `Отправить на CryptoBot?`
                );
                if (!ok) return;
                try {
                    await confirm({
                        request_id: withdrawal.id,
                        currency,
                        account,
                        card,
                        force_cryptobot: true,
                    }).unwrap();
                    showSuccess("Вывод подтверждён");
                } catch (retryError: any) {
                    showError(
                        retryError?.data?.message ||
                            retryError?.message ||
                            "Неизвестная ошибка"
                    );
                }
                return;
            }
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const handleCancel = (withdrawal: WithdrawalRequest, currency: number) => {
        setSelectedWithdrawal(withdrawal);
        setCancelCurrency(currency);
        setShowCancelModal(true);
    };

    const handleCreateAltynCryptoPayout = async (withdrawal: WithdrawalRequest) => {
        if (!window.confirm("Создать Altyn Crypto payout по этой заявке?")) return;
        try {
            await createAltynCryptoPayout({ request_id: withdrawal.id }).unwrap();
            showSuccess("Altyn Crypto payout создан");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const handleCreateAltynSbpPayout = async (withdrawal: WithdrawalRequest) => {
        if (!window.confirm("Создать Altyn SBP payout по этой заявке?")) return;
        try {
            await createAltynSbpPayout({ request_id: withdrawal.id }).unwrap();
            showSuccess("Altyn SBP payout создан");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const submitCancel = async () => {
        if (!selectedWithdrawal || !cancelComment.trim()) {
            alert("Введите причину отмены");
            return;
        }

        try {
            await cancel({
                user_id: selectedWithdrawal.user_id,
                currency: cancelCurrency,
                comment: cancelComment,
            }).unwrap();
            setShowCancelModal(false);
            setCancelComment("");
            setSelectedWithdrawal(null);
            showSuccess("Вывод отменён");
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || "Неизвестная ошибка";
            showError(errorMessage);
        }
    };

    const copyToClipboard = async (text: string): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            try {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                textarea.style.top = "-9999px";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                const ok = document.execCommand("copy");
                document.body.removeChild(textarea);
                return ok;
            } catch {
                return false;
            }
        }
    };

    const CopyIcon = ({ className }: { className?: string }) => (
        <svg
            className={className ?? "w-4 h-4"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );

    const CheckIcon = ({ className }: { className?: string }) => (
        <svg
            className={className ?? "w-4 h-4"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M20 6 9 17l-5-5" />
        </svg>
    );

    const CopyButton = ({
        value,
        label,
    }: {
        value: string;
        label: string;
    }) => {
        const disabled = !value;
        const [copied, setCopied] = React.useState(false);
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={async () => {
                    const ok = await copyToClipboard(value);
                    if (!ok) {
                        alert("Не удалось скопировать");
                        return;
                    }
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 900);
                }}
                className={[
                    "ml-2 inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] transition",
                    disabled
                        ? "cursor-not-allowed border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500"
                        : copied
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800 animate-pulse dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-white/5",
                ].join(" ")}
                title={
                    disabled
                        ? `Нет данных для ${label}`
                        : copied
                        ? `Скопировано: ${label}`
                        : `Скопировать ${label}`
                }
                aria-label={
                    disabled
                        ? `Нет данных для ${label}`
                        : copied
                        ? `Скопировано: ${label}`
                        : `Скопировать ${label}`
                }
            >
                {copied ? (
                    <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                    <CopyIcon className="w-3.5 h-3.5" />
                )}
            </button>
        );
    };

    const parseMethodFlags = (methodFlags?: string | null): string[] => {
        const raw = String(methodFlags || "")
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item === "0" || item === "1");

        if (raw.length === 5) {
            return [...raw, "0", "0"];
        }
        if (raw.length === 7) {
            return raw;
        }
        return [];
    };

    const isMethodSelected = (withdrawal: WithdrawalRequest, idx: number): boolean => {
        const flags = parseMethodFlags(withdrawal.method_flags);
        return flags[idx] === "1";
    };

    const renderMethodBadges = (withdrawal: WithdrawalRequest) => {
        if (!withdrawal.is_frozen) {
            return (
                <button
                    onClick={() => handleFreeze(withdrawal.id)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Показать данные
                </button>
            );
        }

        const flags = parseMethodFlags(withdrawal.method_flags);
        if (flags.length !== 7) {
            return (
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500">—</span>
                    <div className="text-xs text-gray-700 dark:text-gray-200">
                        <span className="font-semibold">Счет:</span>{" "}
                        {withdrawal.bank_account || "—"}
                        <CopyButton
                            value={withdrawal.bank_account || ""}
                            label="банковский счет"
                        />
                    </div>
                </div>
            );
        }

        const labels = ["CryptoBot", "BEP20", "TRC20", "TON", "Card", "AltynCrypto", "AltynSBP"];
        const values = [
            withdrawal.telegram_id || withdrawal.description || "",
            withdrawal.bep20_address || withdrawal.bep20 || "",
            withdrawal.trc20_address || withdrawal.trc20 || "",
            withdrawal.ton_address || withdrawal.ton || "",
            (withdrawal.card_number || withdrawal.bank_card || "") +
            (withdrawal.altyn_sbp_bank_name && flags[4] === "1" ? ` (${withdrawal.altyn_sbp_bank_name})` : ""),
            withdrawal.altyn_crypto_wallet || "",
            withdrawal.altyn_sbp_bank_name || withdrawal.altyn_sbp_bank_id || "",
        ];

        const copyValues = [
            withdrawal.telegram_id || withdrawal.description || "",
            withdrawal.bep20_address || withdrawal.bep20 || "",
            withdrawal.trc20_address || withdrawal.trc20 || "",
            withdrawal.ton_address || withdrawal.ton || "",
            withdrawal.card_number || withdrawal.bank_card || "",
            withdrawal.altyn_crypto_wallet || "",
            withdrawal.altyn_sbp_bank_name || withdrawal.altyn_sbp_bank_id || "",
        ];

        return (
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-1">
                    {flags.map((flag, idx) => {
                        const isSelected = flag === "1";
                        const val = values[idx];
                        const copyVal = copyValues[idx];

                        return (
                            <span
                                key={`${withdrawal.id}-${idx}`}
                                className={[
                                    "inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold",
                                    isSelected
                                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                                        : "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-200",
                                ].join(" ")}
                                title={isSelected ? "Выбрано" : "Не выбрано"}
                            >
                                {labels[idx]}
                                {val ? (
                                    <span className="ml-1 font-normal text-gray-700 dark:text-gray-200">
                                        {val}
                                    </span>
                                ) : (
                                    <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                                        —
                                    </span>
                                )}
                                <CopyButton value={copyVal || ""} label={labels[idx]} />
                            </span>
                        );
                    })}
                </div>

                <div className="text-xs text-gray-700 dark:text-gray-200">
                    <span className="font-semibold">Счет:</span>{" "}
                    {withdrawal.bank_account || "—"}
                    <CopyButton
                        value={withdrawal.bank_account || ""}
                        label="банковский счет"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Управление выводами
            </h1>

            {/* Запросы на вывод USD/USDT */}
            {fiatWithdrawals && fiatWithdrawals.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Запросы на вывод
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        ID
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        ФИО
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        Личники
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        Куда выводить
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        Вывод USD
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        Вывод USDT
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                    >
                                        Telegram
                                    </th>
                                    <th
                                        colSpan={10}
                                        className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800"
                                    >
                                        Всего
                                    </th>
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                                    >
                                        Действия
                                    </th>
                                </tr>
                                <tr>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Рефералов"
                                    >
                                        Реф
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Менеджеров"
                                    >
                                        Мен
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего денег на кошельке USD"
                                    >
                                        USD
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего денег на кошельке USDT"
                                    >
                                        USDT
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего внесено USD"
                                    >
                                        Внес USD
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего внесено USDT"
                                    >
                                        Внес USDT
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего заработано USD"
                                    >
                                        Зараб USD
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Всего заработано USDT"
                                    >
                                        Зараб USDT
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Заработано в месяц USD"
                                    >
                                        Мес USD
                                    </th>
                                    <th
                                        className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-r border-gray-300 dark:border-gray-600"
                                        title="Заработано в месяц USDT"
                                    >
                                        Мес USDT
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {fiatWithdrawals.map((withdrawal) => (
                                    <tr key={withdrawal.id}>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {withdrawal.user_id}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            <img
                                                src={`${withdrawal.photo}`}
                                                alt="User"
                                                className="w-12 h-12 rounded-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedUserId(
                                                        withdrawal.user_id
                                                    );
                                                    setIsUserViewModalOpen(
                                                        true
                                                    );
                                                }}
                                                className="text-left hover:underline text-blue-700 dark:text-blue-300 mb-2"
                                                title="Открыть карточку пользователя"
                                            >
                                                {withdrawal.surname}{" "}
                                                {withdrawal.name}
                                            </button>
                                            {withdrawal.source && withdrawal.source === "hub" && (
                                                <span className="text-sm px-1 border border-gray-400 rounded-md text-gray-800 dark:text-gray-400">{withdrawal.source}</span>
                                            )}
                                            {withdrawal.source && withdrawal.source === "game" && (
                                                <span className="text-sm px-1 border border-green-400 rounded-md text-green-800 dark:text-green-400">{withdrawal.source}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {withdrawal.clients_total}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {renderMethodBadges(withdrawal)}
                                        </td>
                                        {/* Pending суммы */}
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700 bg-yellow-100 dark:bg-yellow-900/40">
                                            {withdrawal.currency === "USD"
                                                ? `$${withdrawal.amount.toFixed(
                                                      2
                                                  )}`
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700 bg-yellow-100 dark:bg-yellow-900/40">
                                            {withdrawal.currency === "USDT"
                                                ? `${withdrawal.amount.toFixed(
                                                      2
                                                  )}₮`
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {withdrawal.is_frozen ? withdrawal.telegram_id : "—"}
                                        </td>
                                        {/* Статистика "Всего" */}
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {withdrawal.total_referrals}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            {withdrawal.managers_total}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            ${withdrawal.main_wallet.toFixed(2)}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            ${withdrawal.usdt_wallet.toFixed(2)}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.total_deposited.toFixed(
                                                2
                                            )}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.usdt_total_deposited.toFixed(
                                                2
                                            )}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.total_earned.toFixed(2)}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.usdt_total_earned.toFixed(
                                                2
                                            )}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.main_monthly_profit.toFixed(
                                                2
                                            )}
                                        </td>
                                        <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                                            $
                                            {withdrawal.usdt_monthly_profit.toFixed(
                                                2
                                            )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                                            <div className="flex flex-col gap-1">
                                                {withdrawal.currency ===
                                                    "USD" && (
                                                    <>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    0,
                                                                    withdrawal.bank_account ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на банковский счет: ${
                                                                withdrawal.bank_account ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            💳 USD на счет
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -2,
                                                                    undefined,
                                                                    withdrawal.bank_card ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-blue-400 hover:bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на банковскую карту: ${
                                                                withdrawal.bank_card ||
                                                                "не указана"
                                                            }`}
                                                        >
                                                            💳 USD на карту
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -3,
                                                                    undefined,
                                                                    withdrawal.bep20 ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-blue-400 hover:bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на BEP20 адрес: ${
                                                                withdrawal.bep20 ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USD BEP20
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -3,
                                                                    undefined,
                                                                    withdrawal.trc20 ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-blue-400 hover:bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на TRC20 адрес: ${
                                                                withdrawal.trc20 ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USD TRC20
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -3,
                                                                    undefined,
                                                                    withdrawal.ton_address ||
                                                                        withdrawal.ton ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-blue-400 hover:bg-blue-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на TON адрес: ${
                                                                withdrawal.ton_address ||
                                                                withdrawal.ton ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USD TON
                                                        </button>
                                                        {isMethodSelected(withdrawal, 5) && (
                                                            <button
                                                                onClick={() =>
                                                                    handleCreateAltynCryptoPayout(
                                                                        withdrawal
                                                                    )
                                                                }
                                                                disabled={isCreatingAltynCryptoPayout}
                                                                className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap disabled:opacity-60"
                                                                title={`Altyn Crypto: ${
                                                                    withdrawal.altyn_crypto_wallet ||
                                                                    "кошелёк не указан"
                                                                }`}
                                                            >
                                                                🟣 Altyn Crypto
                                                            </button>
                                                        )}
                                                        {isMethodSelected(withdrawal, 6) && (
                                                            <button
                                                                onClick={() =>
                                                                    handleCreateAltynSbpPayout(
                                                                        withdrawal
                                                                    )
                                                                }
                                                                disabled={isCreatingAltynSbpPayout}
                                                                className="bg-violet-500 hover:bg-violet-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap disabled:opacity-60"
                                                                title={`Altyn SBP: ${
                                                                    withdrawal.altyn_sbp_bank_name ||
                                                                    withdrawal.altyn_sbp_bank_id ||
                                                                    "банк не указан"
                                                                }`}
                                                            >
                                                                🟪 Altyn SBP
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() =>
                                                                handleCancel(
                                                                    withdrawal,
                                                                    0
                                                                )
                                                            }
                                                            className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                                                        >
                                                            Отменить USD
                                                        </button>
                                                    </>
                                                )}
                                                {withdrawal.currency ===
                                                    "USDT" && (
                                                    <>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    1,
                                                                    withdrawal.telegram_id ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод через CryptoBot: ${
                                                                withdrawal.telegram_id ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🤖 USDT (CryptoBot)
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -1,
                                                                    undefined,
                                                                    withdrawal.bank_card ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-green-400 hover:bg-green-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на банковскую карту: ${
                                                                withdrawal.bank_card ||
                                                                "не указана"
                                                            }`}
                                                        >
                                                            💳 USDT на карту
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -1,
                                                                    undefined,
                                                                    withdrawal.bep20 ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-green-400 hover:bg-green-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на BEP20 адрес: ${
                                                                withdrawal.bep20 ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USDT BEP20
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -1,
                                                                    undefined,
                                                                    withdrawal.trc20 ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-green-400 hover:bg-green-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на TRC20 адрес: ${
                                                                withdrawal.trc20 ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USDT TRC20
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleConfirm(
                                                                    withdrawal,
                                                                    -1,
                                                                    undefined,
                                                                    withdrawal.ton_address ||
                                                                        withdrawal.ton ||
                                                                        undefined
                                                                )
                                                            }
                                                            className="bg-green-400 hover:bg-green-500 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                            title={`Вывод на TON адрес: ${
                                                                withdrawal.ton_address ||
                                                                withdrawal.ton ||
                                                                "не указан"
                                                            }`}
                                                        >
                                                            🔗 USDT TON
                                                        </button>
                                                        {isMethodSelected(withdrawal, 5) && (
                                                            <button
                                                                onClick={() =>
                                                                    handleCreateAltynCryptoPayout(
                                                                        withdrawal
                                                                    )
                                                                }
                                                                disabled={isCreatingAltynCryptoPayout}
                                                                className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap disabled:opacity-60"
                                                                title={`Altyn Crypto: ${
                                                                    withdrawal.altyn_crypto_wallet ||
                                                                    "кошелёк не указан"
                                                                }`}
                                                            >
                                                                🟣 Altyn Crypto
                                                            </button>
                                                        )}
                                                        {isMethodSelected(withdrawal, 6) && (
                                                            <button
                                                                onClick={() =>
                                                                    handleCreateAltynSbpPayout(
                                                                        withdrawal
                                                                    )
                                                                }
                                                                disabled={isCreatingAltynSbpPayout}
                                                                className="bg-violet-500 hover:bg-violet-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap disabled:opacity-60"
                                                                title={`Altyn SBP: ${
                                                                    withdrawal.altyn_sbp_bank_name ||
                                                                    withdrawal.altyn_sbp_bank_id ||
                                                                    "банк не указан"
                                                                }`}
                                                            >
                                                                🟪 Altyn SBP
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() =>
                                                                handleCancel(
                                                                    withdrawal,
                                                                    1
                                                                )
                                                            }
                                                            className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs"
                                                        >
                                                            Отменить USDT
                                                        </button>
                                                    </>
                                                )}
                                                {/* ALNY выводы обрабатываются в отдельной таблице ниже */}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <p className="text-center text-gray-500 dark:text-gray-400">
                        Нет запросов на вывод
                    </p>
                </div>
            )}

            {/* ALNY table */}
            {alnyRows && alnyRows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            ALNY Token — заявки на вывод
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        ID
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        ФИО
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        BEP20
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        ALNY Кошелёк
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        ALNY Pending
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        USD
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        USDT
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Зараб USD
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Зараб USDT
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {alnyRows.map((r: any) => (
                                    <tr
                                        key={r.user_id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <td className="px-3 py-3 text-sm">
                                            {r.user_id}
                                        </td>
                                        <td className="px-3 py-3 text-sm">
                                            {r.surname} {r.name}
                                        </td>
                                        <td className="px-3 py-3 text-sm">
                                            {r.bep20 || "—"}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right">
                                            {r.alny_wallet}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right font-semibold">
                                            {r.pending}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right">
                                            ${(+r.main_wallet || 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right">
                                            ${(+r.usdt_wallet || 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right">
                                            ${(+r.total_earned || 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right">
                                            $
                                            {(
                                                +r.usdt_total_earned || 0
                                            ).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-3 text-sm">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() =>
                                                        handleConfirmAlny(
                                                            r.user_id
                                                        )
                                                    }
                                                    className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                >
                                                    ✅ Подтвердить
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        handleCancelAlny(
                                                            r.user_id
                                                        )
                                                    }
                                                    className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs whitespace-nowrap"
                                                >
                                                    ❌ Отменить
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {/* История заявок на вывод */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        История заявок на вывод
                    </h2>
                    <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-300">
                                Статус:
                            </span>
                            <select
                                value={historyStatus}
                                onChange={(e) =>
                                    setHistoryStatus(
                                        e.target.value as
                                            | "all"
                                            | WithdrawalStatus
                                    )
                                }
                                className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-sm"
                            >
                                <option value="all">Все</option>
                                <option value="pending">В ожидании</option>
                                <option value="processing">В обработке</option>
                                <option value="completed">Выполнены</option>
                                <option value="cancelled">Отменены</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-300">
                                Валюта:
                            </span>
                            <select
                                value={historyCurrency}
                                onChange={(e) =>
                                    setHistoryCurrency(
                                        e.target.value as "all" | "USD" | "USDT"
                                    )
                                }
                                className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-sm"
                            >
                                <option value="all">Все</option>
                                <option value="USD">USD</option>
                                <option value="USDT">USDT</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 dark:text-gray-300">
                                Источник:
                            </span>
                            <select
                                value={historySource}
                                onChange={(e) =>
                                    setHistorySource(
                                        e.target.value as "all" | "hub" | "game"
                                    )
                                }
                                className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-sm"
                            >
                                <option value="all">Все</option>
                                <option value="hub">Хаб</option>
                                <option value="game">Игра</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    ID
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Пользователь
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Валюта
                                </th>
                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Сумма
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Статус
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Создана
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Обработана / Отменена
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Администратор
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {isHistoryLoading ? (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="px-3 py-4 text-center text-sm text-gray-600 dark:text-gray-300"
                                    >
                                        Загрузка истории...
                                    </td>
                                </tr>
                            ) : historyItems.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="px-3 py-4 text-center text-sm text-gray-600 dark:text-gray-300"
                                    >
                                        История заявок пуста.
                                    </td>
                                </tr>
                            ) : (
                                historyItems.map((item) => (
                                    <tr key={item.id}>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.id}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.user_surname ?? item.surname}{" "}
                                            {item.user_name ?? item.name}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.currency}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right text-gray-900 dark:text-white">
                                            {item.amount.toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.status}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.created_at
                                                ? new Date(
                                                      item.created_at
                                                  ).toLocaleString()
                                                : "-"}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.processed_at
                                                ? new Date(
                                                      item.processed_at
                                                  ).toLocaleString()
                                                : item.cancelled_at
                                                ? new Date(
                                                      item.cancelled_at
                                                  ).toLocaleString()
                                                : "-"}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                            {item.admin_id
                                                ? `${
                                                      item.admin_surname ?? ""
                                                  } ${item.admin_name ?? ""}`
                                                : "-"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {historyMeta && historyMeta.last_page > 1 && (
                    <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
                        <div>
                            Страница {historyMeta.current_page} из{" "}
                            {historyMeta.last_page}, всего {historyMeta.total}{" "}
                            записей
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={historyMeta.current_page <= 1}
                                onClick={() =>
                                    setHistoryPage(
                                        Math.max(
                                            1,
                                            historyMeta.current_page - 1
                                        )
                                    )
                                }
                                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                            >
                                Назад
                            </button>
                            <button
                                disabled={
                                    historyMeta.current_page >=
                                    historyMeta.last_page
                                }
                                onClick={() =>
                                    setHistoryPage(
                                        Math.min(
                                            historyMeta.last_page,
                                            historyMeta.current_page + 1
                                        )
                                    )
                                }
                                className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                            >
                                Вперёд
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal для отмены */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                            Отмена вывода
                        </h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Причина отмены:
                            </label>
                            <textarea
                                value={cancelComment}
                                onChange={(e) =>
                                    setCancelComment(e.target.value)
                                }
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={4}
                                placeholder="Введите причину отмены..."
                            />
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={submitCancel}
                                disabled={isCancelling || !cancelComment.trim()}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                            >
                                {isCancelling ? "Отмена..." : "Отменить вывод"}
                            </button>
                            <button
                                onClick={() => {
                                    setShowCancelModal(false);
                                    setCancelComment("");
                                    setSelectedWithdrawal(null);
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <UserViewModal
                userId={selectedUserId}
                isOpen={isUserViewModalOpen}
                onClose={() => {
                    setIsUserViewModalOpen(false);
                    setSelectedUserId(null);
                }}
            />

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};
